import type {
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
  RouteLocationRaw,
  Router,
} from 'vue-router';
import { singleQueryValue } from '@shared/libs/route-query';

// `sharedReview` and `signals` are taken: the peer review and signals drawers read
// those on this route.
const REVIEW_PARAM = 'connectionReview';

// The review button opens the drawer with no review in hand, and the current
// review's id is not known until /current loads.
const CURRENT_REVIEW = 'current';

const PROFILE_ROUTE_NAME = 'suppliers-profile';

type ProfileLocation = Pick<
  RouteLocationNormalized,
  'path' | 'query' | 'hash' | 'params' | 'matched'
>;

function profileID(route: ProfileLocation): string | undefined {
  if (!route.matched.some((record) => record.name === PROFILE_ROUTE_NAME)) return undefined;
  const { id } = route.params;
  return typeof id === 'string' ? id : undefined;
}

// The supplier review drawer should remain open for as long as the viewer is on
// the supplier's profile
export function carryOpenReviewWithinProfile(
  to: ProfileLocation,
  from: ProfileLocation,
): RouteLocationRaw | undefined {
  if (to.path === from.path) return undefined;

  const profile = profileID(to);
  if (profile === undefined || profile !== profileID(from)) return undefined;

  const review = singleQueryValue(from.query, REVIEW_PARAM);
  if (review === undefined) return undefined;
  if (singleQueryValue(to.query, REVIEW_PARAM) !== undefined) return undefined;

  return { path: to.path, query: { ...to.query, [REVIEW_PARAM]: review }, hash: to.hash };
}

// Holds the review drawer's open state on the route so an email link can open it.
// Pushed rather than replaced, so the back button closes it.
export function useConnectionReviewDrawerRoute(
  route: Pick<RouteLocationNormalizedLoaded, 'query'>,
  router: Pick<Router, 'push'>,
) {
  // Merged onto the query already on the route, so the other drawers' parameters
  // survive. No review closes the drawer: the router drops an undefined parameter.
  function show(review?: string) {
    void router.push({ query: { ...route.query, [REVIEW_PARAM]: review } });
  }

  const isDrawerOpen = computed({
    get: () => singleQueryValue(route.query, REVIEW_PARAM) !== undefined,
    set: (open) => show(open ? CURRENT_REVIEW : undefined),
  });

  // Undefined is whichever review is current, which the route carries as its own
  // value: the drawer has to stay open on a review whose id it does not yet know.
  const openReviewID = computed({
    get() {
      const review = singleQueryValue(route.query, REVIEW_PARAM);
      return review === CURRENT_REVIEW ? undefined : review;
    },
    set: (review) => show(review ?? CURRENT_REVIEW),
  });

  return { isDrawerOpen, openReviewID };
}
